import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import Papa from "papaparse";
import { useEffect, useState } from "react";
import useColors from "../../../../api/hooks/useColors";
import { entityKeys } from "../../../../api/queryKeys";
import ContainedButton from "../../../../components/buttons/ContainedButton";
import { useFileNaming } from "../../../../hooks/useFileNaming";
import EntityFieldMultiSelect from "./EntityFieldMultiSelect";
import ExportPreview from "./ExportPreview";
import { formatFieldName, generateMockData } from "./exportPreviewUtils";
import TenantMultiSelect from "./TenantMultiSelect";
import VulnerabilityFieldMultiSelect from "./VulnerabilityFieldMultiSelect";

const ExportVulnerabilitiesModal = ({
  open,
  onClose,
  onExport,
  isLoading,
  tenants = [],
  msspId,
  platformSelectedTenantIds = [],
}) => {
  const colors = useColors();
  const queryClient = useQueryClient();
  const { generateCsvFileName } = useFileNaming();
  const [selectedTenantIds, setSelectedTenantIds] = useState(() =>
    msspId ? [...new Set([msspId, ...platformSelectedTenantIds])] : []
  );
  const [selectedVulnerabilityFields, setSelectedVulnerabilityFields] =
    useState([]);
  const [selectedEntityFields, setSelectedEntityFields] = useState([]);

  useEffect(() => {
    if (open) {
      setSelectedTenantIds(
        msspId ? [...new Set([msspId, ...platformSelectedTenantIds])] : []
      );
      setSelectedVulnerabilityFields([]);
      setSelectedEntityFields([]);
    }
  }, [open, msspId, platformSelectedTenantIds]);

  const handleTenantSelectionChanged = (newTenantIds) => {
    queryClient.invalidateQueries({
      queryKey: entityKeys.fieldsForExport(newTenantIds),
    });
  };

  const handleClose = () => {
    onClose();
  };

  const handleExport = () => {
    onExport(
      selectedTenantIds,
      selectedVulnerabilityFields,
      selectedEntityFields
    );
  };

  const handleDownloadPreview = () => {
    const allFields = [...selectedVulnerabilityFields, ...selectedEntityFields];
    const headers = [
      "Tenant",
      ...allFields.map((field) => formatFieldName(field)),
    ];
    const mockRow = [
      "tenant_123",
      ...allFields.map((field) => generateMockData(field)),
    ];
    const csvData = [headers, mockRow];
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const csvURL = window.URL.createObjectURL(blob);
    const tempLink = document.createElement("a");
    tempLink.href = csvURL;
    tempLink.setAttribute(
      "download",
      generateCsvFileName("vulnerabilities_preview")
    );
    tempLink.click();
    tempLink.remove();
    window.URL.revokeObjectURL(csvURL);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "10px",
          minWidth: "700px",
          minHeight: "500px",
        },
      }}
    >
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <DialogTitle>
          <Typography variant="h6">Export Vulnerabilities</Typography>
        </DialogTitle>
        <Box
          onClick={handleClose}
          paddingRight={2}
          display="flex"
          justifyContent="center"
          sx={{ cursor: "pointer" }}
        >
          <ClearRoundedIcon
            sx={{
              width: "20px",
              height: "20px",
              color: colors.action_active,
            }}
          />
        </Box>
      </Box>
      <DialogContent sx={{ padding: 3, paddingTop: 0, paddingBottom: 3 }}>
        {tenants.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <TenantMultiSelect
              tenants={tenants}
              selectedTenantIds={selectedTenantIds}
              onSelectionChange={setSelectedTenantIds}
              onSelectionChanged={handleTenantSelectionChanged}
            />
          </Box>
        )}

        <Box sx={{ mb: 3 }}>
          <VulnerabilityFieldMultiSelect
            selectedFields={selectedVulnerabilityFields}
            onSelectionChange={setSelectedVulnerabilityFields}
            label="Vulnerability Fields (optional)"
          />
        </Box>

        <EntityFieldMultiSelect
          selectedFields={selectedEntityFields}
          onSelectionChange={setSelectedEntityFields}
          label="Entity Fields (optional)"
          tenantIds={selectedTenantIds}
        />

        <ExportPreview
          selectedVulnerabilityFields={selectedVulnerabilityFields}
          selectedEntityFields={selectedEntityFields}
          onDownloadPreview={handleDownloadPreview}
        />
      </DialogContent>
      <DialogActions sx={{ padding: "16px 24px" }}>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          sx={{ textTransform: "none" }}
        >
          <Typography
            variant="subtitle2"
            sx={{ textTransform: "none", color: colors.text_secondary }}
          >
            Cancel
          </Typography>
        </Button>
        <Box sx={{ flex: 1 }} />
        <ContainedButton
          onClick={handleExport}
          disabled={isLoading}
          autoFocus
          color="primary"
          size="small"
          loading={isLoading}
        >
          <Typography variant="subtitle2" sx={{ textTransform: "none" }}>
            {isLoading ? "Exporting..." : "Export"}
          </Typography>
        </ContainedButton>
      </DialogActions>
    </Dialog>
  );
};

export default ExportVulnerabilitiesModal;
